import { graphql as gql } from "@/generated";
import { useAuthStore } from "@/stores/auth/auth.store";
import { SimpleObservable, SimpleObservableSubject } from "@/utils/observable";
import { toValueReal } from "@/utils/reactivity";
import { graphQlClient } from "@/utils/urql";
import {
  formatMessage,
  type SIWEConfig,
  type SIWECreateMessageArgs,
  type SIWESession,
  type SIWEVerifyMessageArgs,
} from "@reown/appkit-siwe";
import { chiliz } from "viem/chains";

/**
 * Normalizes a chain ID string to a number.
 * It handles both the EIP-155 format (e.g., "eip155:1") and plain numeric strings (e.g., "1").
 * If the string is not in a valid format, it throws an error.
 */
function normalizeChainId(id: string): number {
  let value: number;
  const separatorIndex = id.indexOf(":");

  if (separatorIndex === -1) {
    value = parseInt(id);
  } else {
    const numPart = id.slice(separatorIndex + 1);

    if (numPart.length > 0) value = parseInt(numPart);
    else throw new Error("Invalid chain ID");
  }

  if (isNaN(value)) {
    throw new Error("Invalid chain ID");
  }

  return value;
}

const MyWalletsForSessionDocument = gql(/* GraphQL */ `
  query MyWalletsForSession {
    myWallets {
      ... on UserWebThreeWalletList {
        wallets {
          address
          chain_id
        }
      }
      ... on UnauthenticatedError {
        error
      }
    }
  }
`);

const ConnectWallet = gql(/* GraphQL */ `
  mutation ConnectWallet($message: String!, $signature: String!) {
    connectWallet(message: $message, signature: $signature)
  }
`);

export class AppSiwe extends SimpleObservable implements SIWEConfig {
  private _token: string | null = null;
  public onWebThreeToken: SimpleObservableSubject<string | null>;
  public onWalletConnectedInSession: SimpleObservableSubject<boolean>;

  constructor(
    onWebThreeToken = new SimpleObservableSubject<string | null>(),
    onWalletConnectedInSession = new SimpleObservableSubject<boolean>({
      canHoldState: false,
    }),
  ) {
    super();
    this.onWebThreeToken = onWebThreeToken;
    this.onWalletConnectedInSession = onWalletConnectedInSession;
  }

  get webThreeToken(): string | null {
    return this._token;
  }

  set webThreeToken(value: string | null) {
    this._token = value;
  }

  getMessageParams = async () => ({
    domain: window.location.host,
    uri: window.location.origin,
    chains: [chiliz.id],
    statement: "Please sign with your account",
  });

  getNonce = async () => Math.round(Math.random() * 10000).toString();

  createMessage = ({ address, ...args }: SIWECreateMessageArgs) =>
    formatMessage(args, address);

  getSession = async (): Promise<SIWESession | null> => {
    // Check transient in-memory token (set during current session)
    if (this._token) {
      this.notify(this.onWebThreeToken, this._token);
      return {
        address: "" as `0x${string}`,
        chainId: chiliz.id,
      };
    }

    // Fallback: check DB for a persisted wallet (survives page reloads)
    // Only query if authenticated — unauthenticated contexts (e.g. Simulfest puppeteer)
    // would get a 400 from the myWallets query and throw an unhandled rejection.
    const authStore = useAuthStore();
    if (!authStore.hasAuthToken) return null;

    try {
      const result = await graphQlClient.value.query(
        MyWalletsForSessionDocument,
        {},
      );
      const walletsResult = result.data?.myWallets;
      if (walletsResult && !("error" in walletsResult)) {
        const wallet = walletsResult.wallets?.[0];
        if (wallet) {
          return {
            address: wallet.address as `0x${string}`,
            chainId: normalizeChainId(wallet.chain_id),
          };
        }
      }
    } catch {
      // Network error on page load — return null, AppKit will show connect button
    }

    return null;
  };

  verifyMessage = async ({ message, signature }: SIWEVerifyMessageArgs) => {
    const result = await graphQlClient.value.mutation(ConnectWallet, {
      message: toValueReal(message),
      signature: toValueReal(signature),
    });

    const data = result.data?.connectWallet;

    if (!data) return false;

    this._token = data;
    this.notify(this.onWalletConnectedInSession, true);
    this.notify(this.onWebThreeToken, data);

    return true;
  };

  signOut = async () => {
    this._token = null;
    this.notify(this.onWebThreeToken, null);
    return true;
  };
}
